import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link as MuiLink
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { additionalPaymentService } from '../../services/additionalPaymentService';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const AdditionalPaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payment, setPayment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);

  useEffect(() => {
    loadPayment();
  }, [id]);

  const loadPayment = async () => {
    try {
      setLoading(true);
      const response = await additionalPaymentService.getById(parseInt(id!));
      setPayment(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setActionLoading(true);
      await additionalPaymentService.markAsPaid(parseInt(id!));
      await loadPayment();
      setConfirmDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al confirmar el pago');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await additionalPaymentService.delete(parseInt(id!));
      navigate('/additional-payments');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar el pago');
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = () => {
    const subject = `Comprobante de Pago - ${payment?.concept}`;
    const body = `Detalles del pago:\n` +
      `Concepto: ${payment?.concept}\n` +
      `Estudiante: ${payment?.student?.first_name} ${payment?.student?.last_name}\n` +
      `Monto: ${payment?.currency === 'USD' ? '$' : payment?.currency === 'EUR' ? '€' : '₽'} ${payment?.amount}\n` +
      `Fecha: ${dayjs(payment?.payment_date).format('DD/MM/YYYY')}\n` +
      `Referencia: ${payment?.reference_number || 'N/A'}`;
    
    window.location.href = `mailto:${payment?.parent?.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSendWhatsApp = () => {
    const phone = payment?.parent?.phone;
    if (!phone) return;
    
    const message = `*Comprobante de Pago*%0a` +
      `Concepto: ${payment?.concept}%0a` +
      `Estudiante: ${payment?.student?.first_name} ${payment?.student?.last_name}%0a` +
      `Monto: ${payment?.currency === 'USD' ? '$' : payment?.currency === 'EUR' ? '€' : '₽'} ${payment?.amount}%0a` +
      `Fecha: ${dayjs(payment?.payment_date).format('DD/MM/YYYY')}`;
    
    window.open(`https://wa.me/${phone.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pagado', color: 'success' as const, icon: <CheckCircleIcon /> };
      case 'pending':
        return { label: 'Pendiente', color: 'warning' as const, icon: <ErrorIcon /> };
      case 'cancelled':
        return { label: 'Cancelado', color: 'error' as const, icon: <WarningIcon /> };
      default:
        return { label: status, color: 'default' as const, icon: <ReceiptIcon /> };
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      cash: 'Efectivo',
      bank_transfer: 'Transferencia Bancaria',
      card: 'Tarjeta',
      other: 'Otro'
    };
    return methods[method] || method || 'No especificado';
  };

  const formatCurrency = (amount: number, currency: string = 'RUB') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽';
    return `${symbol} ${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box m={2}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/additional-payments')}>
            Volver a pagos
          </Button>
        }>
          {error || 'Pago no encontrado'}
        </Alert>
      </Box>
    );
  }

  const statusInfo = getStatusInfo(payment.status);
  const isPending = payment.status === 'pending';

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <IconButton component={Link} to="/additional-payments" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'secondary.main' }}>
            <ReceiptLongIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {payment.concept}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {payment.student?.first_name} {payment.student?.last_name}
            </Typography>
          </Box>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            sx={{ ml: { xs: 0, sm: 2 } }}
          />
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Tooltip title="Imprimir">
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Enviar por email">
            <IconButton onClick={handleSendEmail} disabled={!payment.parent?.email}>
              <EmailIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Enviar por WhatsApp">
            <IconButton onClick={handleSendWhatsApp} disabled={!payment.parent?.phone}>
              <WhatsAppIcon />
            </IconButton>
          </Tooltip>
          
          {payment.receipt_url && (
            <Tooltip title="Ver comprobante">
              <IconButton onClick={() => setProofDialogOpen(true)}>
                <ReceiptIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {isPending && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setConfirmDialogOpen(true)}
              disabled={actionLoading}
            >
              Marcar como Pagado
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/additional-payments/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
          
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Eliminar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Información del Estudiante */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Estudiante
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Nombre Completo
                      </TableCell>
                      <TableCell>
                        {payment.student?.first_name} {payment.student?.last_name}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Grado
                      </TableCell>
                      <TableCell>{payment.student?.grade || 'No especificado'}</TableCell>
                    </TableRow>
                    {payment.student?.sponsor && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Patrocinador
                        </TableCell>
                        <TableCell>{payment.student.sponsor}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Información del Padre */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Padre/Tutor
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Nombre
                      </TableCell>
                      <TableCell>
                        {payment.parent?.spanish_name || payment.parent?.russian_name || 'No especificado'}
                      </TableCell>
                    </TableRow>
                    {payment.parent?.email && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Email
                        </TableCell>
                        <TableCell>
                          <MuiLink href={`mailto:${payment.parent.email}`}>
                            {payment.parent.email}
                          </MuiLink>
                        </TableCell>
                      </TableRow>
                    )}
                    {payment.parent?.phone && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Teléfono
                        </TableCell>
                        <TableCell>
                          <MuiLink href={`tel:${payment.parent.phone}`}>
                            {payment.parent.country_code} {payment.parent.phone}
                          </MuiLink>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles del Pago */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles del Pago
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '30%', bgcolor: 'action.hover' }}>
                        Concepto
                      </TableCell>
                      <TableCell>
                        <Chip label={payment.concept} color="primary" size="medium" />
                      </TableCell>
                    </TableRow>
                    
                    {payment.description && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Descripción
                        </TableCell>
                        <TableCell>{payment.description}</TableCell>
                      </TableRow>
                    )}
                    
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Monto
                      </TableCell>
                      <TableCell>
                        <Typography variant="h5" color="success.main" fontWeight="bold">
                          {formatCurrency(payment.amount, payment.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Fecha de Pago
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {dayjs(payment.payment_date).format('dddd, D [de] MMMM [de] YYYY')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Método de Pago
                      </TableCell>
                      <TableCell>
                        {getPaymentMethodLabel(payment.payment_method)}
                      </TableCell>
                    </TableRow>
                    
                    {payment.reference_number && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Número de Referencia
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {payment.reference_number}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {payment.comments && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Comentarios
                        </TableCell>
                        <TableCell>{payment.comments}</TableCell>
                      </TableRow>
                    )}
                    
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Estado
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          color={statusInfo.color}
                        />
                      </TableCell>
                    </TableRow>

                    {payment.receipt_url && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Comprobante
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReceiptIcon />}
                            onClick={() => setProofDialogOpen(true)}
                          >
                            Ver comprobante
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Información de Seguimiento */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de Seguimiento
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado por
                  </Typography>
                  <Typography>
                    {payment.creator?.name || 'Sistema'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
                
                {payment.approved_by && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Confirmado por
                    </Typography>
                    <Typography>
                      {payment.approver?.name || 'Administrador'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(payment.approved_at).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Grid>
                )}
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography>
                    {dayjs(payment.updated_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para confirmar pago */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Pago</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea marcar este pago como pagado?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Concepto:</strong> {payment.concept}
            </Typography>
            <Typography variant="body2">
              <strong>Estudiante:</strong> {payment.student?.first_name} {payment.student?.last_name}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {formatCurrency(payment.amount, payment.currency)}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Al confirmar, este pago se registrará en el balance general.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirm} color="success" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea eliminar este pago?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Concepto:</strong> {payment.concept}
            </Typography>
            <Typography variant="body2">
              <strong>Estudiante:</strong> {payment.student?.first_name} {payment.student?.last_name}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {formatCurrency(payment.amount, payment.currency)}
            </Typography>
          </Alert>
          {payment.status === 'paid' && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Este pago ya fue confirmado y registrado en el balance. Al eliminarlo, se revertirá automáticamente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para ver comprobante */}
      <Dialog open={proofDialogOpen} onClose={() => setProofDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Comprobante de Pago</DialogTitle>
        <DialogContent>
          {payment?.receipt_url && (
            <Box sx={{ textAlign: 'center' }}>
              {payment.receipt_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={payment.receipt_url}
                  alt="Comprobante"
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ height: '60vh' }}>
                  <iframe
                    src={payment.receipt_url}
                    title="Comprobante"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} href={payment?.receipt_url} download>
            Descargar
          </Button>
          <Button onClick={() => setProofDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdditionalPaymentDetail;